import { useMemo } from "react"

import type { RangePackage } from "../../types"
import {
  sameCounts,
  sumCounts,
  summarizeVariant,
  variantCounts,
  type ListCounts,
  type ProductSettingsDraft,
  type SavedProductSettings,
  type VariantDraft,
  type VariantSummary,
} from "./productSettingsModel"
import { storeGroupKey, storeScopeId, type StoreGroupStore, type StoreGroups } from "./storeGroups"
import { packageScopeId, type ProductListing } from "./useProductSettings"

/** How the list rolls up: under each package that carries a variant, its product, each store, or flat. */
export type GroupBy = "package" | "product" | "store" | "variant"

/** The store or package group a row sits in; the dialog opens on its stores. */
export type RowScope = { kind: "store" | "package"; id: string }

export type GroupRow = {
  kind: "group"
  id: string
  path: string[]
  groupBy: Exclude<GroupBy, "variant">
  label: string
  variantCount: number
  counts: ListCounts
  /** A store not expanded yet: its counts are zeros until its variants are summed on open. */
  pending: boolean
  /** On a store group: the AllocationStore it lists. */
  storeId?: string
}
export type VariantRow = {
  kind: "variant"
  id: string
  path: string[]
  listing: ProductListing
  summary: VariantSummary
  counts: ListCounts
  packageNames: string[]
  scope?: RowScope
}
/** The one child of a store not expanded yet, so it can expand; its variants replace it on open. */
export type PlaceholderRow = { kind: "placeholder"; id: string; path: string[] }
export type TotalRow = {
  kind: "total"
  id: string
  path: string[]
  variantCount: number
  counts: ListCounts
}
export type Row = GroupRow | VariantRow | PlaceholderRow | TotalRow

/**
 * An edit touches one variant; the others keep their summary and row objects, so the grid only
 * redraws what changed instead of diffing ~100k fresh rows. New caches for new saved settings or
 * listings, when every summary changes anyway.
 */
export type RowCaches = {
  saved: SavedProductSettings | null
  /** The variants the table lists: every store's, or the package listings. */
  listings: ReadonlyMap<string, ProductListing>
  /** Package groups always count what their package sends, whichever stores the table lists. */
  packaged: ReadonlyMap<string, ProductListing>
  scoped: ReadonlyMap<string, ProductListing>
  /** Every listing above by scope id. */
  byScope: ReadonlyMap<string, ProductListing>
  summaries: Map<string, { entry: VariantDraft | undefined; summary: VariantSummary }>
  variantRows: Map<string, VariantRow>
  groupRows: Map<string, GroupRow>
}

export function rowCachesOf(
  saved: SavedProductSettings | null,
  listings: ReadonlyMap<string, ProductListing>,
  packaged: ReadonlyMap<string, ProductListing>,
  scoped: ReadonlyMap<string, ProductListing>,
): RowCaches {
  const byScope = new Map<string, ProductListing>()
  for (const map of [listings, packaged, scoped]) {
    for (const listing of map.values()) byScope.set(listing.scopeId, listing)
  }
  return {
    saved,
    listings,
    packaged,
    scoped,
    byScope,
    summaries: new Map(),
    variantRows: new Map(),
    groupRows: new Map(),
  }
}

export function useRowCaches(
  saved: SavedProductSettings | null,
  listings: ReadonlyMap<string, ProductListing>,
  packaged: ReadonlyMap<string, ProductListing>,
  scoped: ReadonlyMap<string, ProductListing>,
): RowCaches {
  return useMemo(
    () => rowCachesOf(saved, listings, packaged, scoped),
    [saved, listings, packaged, scoped],
  )
}

/**
 * Every listing's summary by scope id under the draft, the open stores' included; a variant whose
 * draft entry is unchanged keeps its summary.
 */
export function summariesOf(
  caches: RowCaches,
  draft: ProductSettingsDraft,
  storeListings: ReadonlyMap<string, ProductListing>,
): Map<string, VariantSummary> {
  const out = new Map<string, VariantSummary>()
  const { saved, byScope, summaries } = caches
  if (!saved) return out
  for (const listing of [...byScope.values(), ...storeListings.values()]) {
    const entry = draft[listing.variantId]
    const hit = summaries.get(listing.scopeId)
    if (hit && hit.entry === entry) {
      out.set(listing.scopeId, hit.summary)
      continue
    }
    const summary = summarizeVariant(listing, saved, entry)
    summaries.set(listing.scopeId, { entry, summary })
    out.set(listing.scopeId, summary)
  }
  return out
}

export function useVariantSummaries(
  caches: RowCaches,
  draft: ProductSettingsDraft,
  storeListings: ReadonlyMap<string, ProductListing>,
) {
  return useMemo(() => summariesOf(caches, draft, storeListings), [caches, draft, storeListings])
}

/** By product and variant name; variants the catalogue doesn't hold (named by id) go last. */
export function byCatalogueName(a: ProductListing, b: ProductListing) {
  const unknown = (listing: ProductListing) => (listing.productName === listing.variantId ? 1 : 0)
  return unknown(a) - unknown(b) || a.name.localeCompare(b.name)
}

type Group = {
  key: string
  label: string
  groupBy: Exclude<GroupBy, "variant">
  members: { variantId: string; scopeId: string }[]
  scope?: RowScope
  storeId?: string
  /** A store not open yet: how many visible variants it lists, without building them. */
  closedCount?: number
}

export type ProductRowsInput = {
  caches: RowCaches
  summaries: ReadonlyMap<string, VariantSummary>
  storeGroups: Pick<StoreGroups, "openStores" | "variantIdsByStore" | "storeListings">
  /** The listed variants in table order. */
  ordered: readonly ProductListing[]
  packages: Pick<RangePackage, "id" | "name" | "productIds">[]
  stores: readonly StoreGroupStore[]
  packageNamesOf: ReadonlyMap<string, string[]>
  groupBy: GroupBy
  visibleVariants: ReadonlySet<string>
}

/** The groups a grouping lists, in order. A store only lists its variants once it is open. */
function groupsFor(groupBy: Exclude<GroupBy, "variant">, input: ProductRowsInput): Group[] {
  const { caches, storeGroups, ordered, packages, stores, visibleVariants } = input
  if (groupBy === "package") {
    return packages.map((pkg) => ({
      key: `package:${pkg.id}`,
      label: pkg.name,
      groupBy,
      scope: { kind: "package", id: pkg.id },
      members: pkg.productIds.map((variantId) => {
        const scopeId = packageScopeId(pkg.id, variantId)
        return { variantId, scopeId: caches.scoped.has(scopeId) ? scopeId : variantId }
      }),
    }))
  }
  if (groupBy === "product") {
    return [...Map.groupBy(ordered, (l) => l.productName)].map(([productName, members]) => ({
      key: `product:${productName}`,
      label: productName,
      groupBy,
      members: members.map(({ variantId, scopeId }) => ({ variantId, scopeId })),
    }))
  }
  const allVisible = visibleVariants.size >= ordered.length
  return stores.flatMap((store) => {
    const atStore = storeGroups.variantIdsByStore.get(store.id) ?? []
    const shown = allVisible ? atStore : atStore.filter((id) => visibleVariants.has(id))
    if (shown.length === 0) return []
    const group: Group = {
      key: storeGroupKey(store.id),
      label: store.storeNumber,
      groupBy,
      storeId: store.id,
      scope: { kind: "store", id: store.id },
      members: [],
    }
    if (!storeGroups.openStores.has(store.id)) return [{ ...group, closedCount: shown.length }]
    const members = shown.map((variantId) => ({
      variantId,
      scopeId: storeScopeId(store.id, variantId),
    }))
    return [{ ...group, members }]
  })
}

function groupRowOf(
  groupRows: Map<string, GroupRow>,
  group: Group,
  variantCount: number,
  counts: ListCounts,
  pending: boolean,
): GroupRow {
  const prev = groupRows.get(group.key)
  const row: GroupRow =
    prev &&
    prev.label === group.label &&
    prev.variantCount === variantCount &&
    prev.pending === pending &&
    sameCounts(prev.counts, counts)
      ? prev
      : {
          kind: "group",
          id: group.key,
          path: [group.key],
          groupBy: group.groupBy,
          label: group.label,
          variantCount,
          counts,
          pending,
          ...(group.storeId === undefined ? {} : { storeId: group.storeId }),
        }
  groupRows.set(group.key, row)
  return row
}

const sameScope = (a: RowScope | undefined, b: RowScope | undefined) =>
  a?.kind === b?.kind && a?.id === b?.id

/** The tree rows of a grouping, the pinned total over the visible variants, and their ids. */
export function productRows(input: ProductRowsInput) {
  const { caches, summaries, storeGroups, ordered, packageNamesOf, groupBy, visibleVariants } =
    input
  const { listings, byScope, variantRows, groupRows } = caches
  const memberRow = (
    { variantId, scopeId }: { variantId: string; scopeId: string },
    group?: Group,
  ): VariantRow[] => {
    const listing = byScope.get(scopeId) ?? storeGroups.storeListings.get(scopeId)
    const summary = summaries.get(scopeId)
    if (!listing || !summary) return []
    const path = group ? [group.key, variantId] : [variantId]
    const id = path.join("/")
    const prev = variantRows.get(id)
    const row: VariantRow =
      prev?.summary === summary && sameScope(prev.scope, group?.scope)
        ? prev
        : {
            kind: "variant",
            id,
            path,
            listing,
            summary,
            counts: variantCounts(summary),
            packageNames: packageNamesOf.get(variantId) ?? [],
            ...(group?.scope === undefined ? {} : { scope: group.scope }),
          }
    variantRows.set(id, row)
    return [row]
  }

  const rows: Row[] = []
  if (groupBy === "variant") {
    for (const listing of ordered) rows.push(...memberRow(listing))
  } else {
    for (const group of groupsFor(groupBy, input)) {
      if (group.closedCount !== undefined) {
        rows.push(groupRowOf(groupRows, group, group.closedCount, sumCounts([]), true), {
          kind: "placeholder",
          id: `${group.key}/…`,
          path: [group.key, "…"],
        })
        continue
      }
      const members = group.members.flatMap((member) => memberRow(member, group))
      if (members.length === 0) continue
      const shown = members.filter((row) => visibleVariants.has(row.listing.variantId))
      const counts = sumCounts(shown.map((row) => row.counts))
      rows.push(groupRowOf(groupRows, group, shown.length, counts, false), ...members)
    }
  }
  // A variant in several packages counts once in the total.
  const listed = [...visibleVariants].flatMap((variantId) => {
    const scopeId = listings.get(variantId)?.scopeId
    const summary = scopeId === undefined ? undefined : summaries.get(scopeId)
    return summary ? [variantCounts(summary)] : []
  })
  const totalRow: TotalRow = {
    kind: "total",
    id: "total",
    path: [],
    variantCount: listed.length,
    counts: sumCounts(listed),
  }
  return { rows, totalRow, listedVariantIds: [...visibleVariants] }
}
